import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/classNames.ts';
import { loadPlacesLibrary } from '@/utils/googleMapsLoader.ts';

type PlaceWidgetProps = {
  placeId: string;
  tone: 'light' | 'dark';
  onUnavailable: () => void;
  className?: string;
};

const LOAD_TIMEOUT_MS = 10_000;

const hostStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  border: '1px solid var(--gmp-mat-color-outline-decorative)',
  borderRadius: '14px',
};

const toneStyles: Record<PlaceWidgetProps['tone'], CSSProperties> = {
  light: {
    ...hostStyle,
    '--gmp-mat-font-family': '"DM Sans", sans-serif',
    '--gmp-mat-color-surface': 'var(--cream-2)',
    '--gmp-mat-color-on-surface': 'var(--ink)',
    '--gmp-mat-color-on-surface-variant': '#3F3D3A',
    '--gmp-mat-color-outline-decorative': 'var(--cream-3)',
    '--gmp-mat-color-primary': 'var(--gold-deep)',
    '--gmp-mat-color-secondary-container': 'var(--cream-3)',
    '--gmp-mat-color-on-secondary-container': 'var(--ink)',
    '--gmp-thumbnail-border-radius': '10px',
    colorScheme: 'light',
  } as CSSProperties,
  dark: {
    ...hostStyle,
    '--gmp-mat-font-family': '"DM Sans", sans-serif',
    '--gmp-mat-color-surface': 'var(--surface)',
    '--gmp-mat-color-on-surface': 'var(--cream)',
    '--gmp-mat-color-on-surface-variant': 'rgba(250, 247, 242, 0.75)',
    '--gmp-mat-color-outline-decorative': 'var(--line-soft)',
    '--gmp-mat-color-primary': 'var(--gold)',
    '--gmp-mat-color-secondary-container': 'var(--surface-2)',
    '--gmp-mat-color-on-secondary-container': 'var(--cream)',
    '--gmp-thumbnail-border-radius': '10px',
    colorScheme: 'dark',
  } as CSSProperties,
};

const PlaceWidget: FC<PlaceWidgetProps> = ({ placeId, tone, onUnavailable, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLElement>(null);
  const [isNear, setIsNear] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isNear) return;

    let isCurrent = true;

    loadPlacesLibrary()
      .then(() => {
        if (isCurrent) setIsReady(true);
      })
      .catch(() => {
        if (isCurrent) onUnavailable();
      });

    return () => {
      isCurrent = false;
    };
  }, [isNear, onUnavailable]);

  useEffect(() => {
    if (!isNear || isLoaded) return;

    const timeout = window.setTimeout(onUnavailable, LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [isNear, isLoaded, onUnavailable]);

  useEffect(() => {
    const element = elementRef.current;
    if (!isReady || !element) return;

    const handleLoad = () => setIsLoaded(true);
    const handleError = () => onUnavailable();

    element.addEventListener('gmp-load', handleLoad);
    element.addEventListener('gmp-error', handleError);

    return () => {
      element.removeEventListener('gmp-load', handleLoad);
      element.removeEventListener('gmp-error', handleError);
    };
  }, [isReady, onUnavailable]);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full max-w-[300px] md:w-60 md:max-w-none', !isLoaded && 'min-h-[260px]', className)}
    >
      {!isLoaded ? (
        <div
          aria-busy="true"
          className={cn(
            'absolute inset-0 animate-pulse rounded-[14px]',
            tone === 'dark' ? 'bg-white/15' : 'bg-primary/20'
          )}
        />
      ) : null}
      {isReady ? (
        <div
          className={cn(
            'transition-opacity duration-200 ease-[cubic-bezier(.22,1,.36,1)]',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
        >
          <gmp-place-details-compact ref={elementRef} orientation="vertical" style={toneStyles[tone]}>
            <gmp-place-details-place-request place={placeId} />
            <gmp-place-content-config>
              <gmp-place-media lightbox-preferred="" />
              <gmp-place-rating />
              <gmp-place-type />
              <gmp-place-attribution light-scheme-color="gray" dark-scheme-color="white" />
            </gmp-place-content-config>
          </gmp-place-details-compact>
        </div>
      ) : null}
    </div>
  );
};

export default PlaceWidget;
